import { createNoise3D, type NoiseFunction3D } from "simplex-noise";
import type { GameConfig } from "../config/GameConfig";
import { clamp, normalize } from "../core/math";
import type { WorldBounds } from "../core/WorldBounds";
import type { Divider } from "../entities/Divider";
import { LowerOcean } from "../env/LowerOcean";
import { OvalVortex } from "../env/OvalVortex";
import { UpperAtmosphere } from "../env/UpperAtmosphere";

export class ForceField {
  readonly cols: number;
  readonly rows: number;
  /** Decaying player current vectors, packed [vx, vy, ...]. */
  readonly stored: Float32Array;

  private readonly noise: NoiseFunction3D;
  private readonly upper: UpperAtmosphere;
  private readonly ocean: LowerOcean;
  private readonly vortex: OvalVortex;

  constructor(
    private readonly config: GameConfig,
    private readonly bounds: WorldBounds,
  ) {
    this.cols = config.field.cols;
    this.rows = config.field.rows;
    this.stored = new Float32Array(this.cols * this.rows * 2);
    this.noise = createNoise3D();
    this.upper = new UpperAtmosphere(config, this.noise);
    this.ocean = new LowerOcean(config, this.noise);
    this.vortex = new OvalVortex(config, this.noise);
  }

  cellIndex(col: number, row: number): number {
    return (row * this.cols + col) * 2;
  }

  worldToCell(x: number, y: number): { col: number; row: number; fx: number; fy: number } {
    const colF = (x / this.bounds.width) * (this.cols - 1);
    const rowF = (y / this.bounds.height) * (this.rows - 1);
    const col = clamp(Math.floor(colF), 0, this.cols - 2);
    const row = clamp(Math.floor(rowF), 0, this.rows - 2);
    return { col, row, fx: colF - col, fy: rowF - row };
  }

  update(dt: number): void {
    const decay = Math.exp(-this.config.playerCurrent.decay * dt);
    const maxStored = this.config.field.maxStored;
    for (let i = 0; i < this.stored.length; i++) {
      this.stored[i] *= decay;
      this.stored[i] = clamp(this.stored[i], -maxStored, maxStored);
    }
  }

  /**
   * Stroke injects a jet in `dir`. Intensity scales with pointer acceleration.
   */
  injectStroke(
    x: number,
    y: number,
    dirX: number,
    dirY: number,
    intensity = 1,
  ): void {
    const dir = normalize(dirX, dirY);
    if (dir.x === 0 && dir.y === 0) return;
    if (intensity <= 0) return;

    const cfg = this.config.playerCurrent;
    const push = cfg.pushStrength * intensity;
    const radius = cfg.strokeRadius;
    const radius2 = radius * radius;
    const cellW = this.bounds.width / Math.max(1, this.cols - 1);
    const cellH = this.bounds.height / Math.max(1, this.rows - 1);

    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const cx = col * cellW;
        const cy = row * cellH;
        const dx = cx - x;
        const dy = cy - y;
        const d2 = dx * dx + dy * dy;
        if (d2 > radius2) continue;
        const dist = Math.sqrt(d2);
        const falloff = 1 - dist / radius;
        const soft = falloff * falloff;

        const i = this.cellIndex(col, row);
        this.stored[i] += dir.x * push * soft;
        this.stored[i + 1] += dir.y * push * soft;
      }
    }
  }

  sampleStored(x: number, y: number): { x: number; y: number } {
    const { col, row, fx, fy } = this.worldToCell(x, y);
    const i00 = this.cellIndex(col, row);
    const i10 = this.cellIndex(col + 1, row);
    const i01 = this.cellIndex(col, row + 1);
    const i11 = this.cellIndex(col + 1, row + 1);

    const vx =
      this.stored[i00] * (1 - fx) * (1 - fy) +
      this.stored[i10] * fx * (1 - fy) +
      this.stored[i01] * (1 - fx) * fy +
      this.stored[i11] * fx * fy;
    const vy =
      this.stored[i00 + 1] * (1 - fx) * (1 - fy) +
      this.stored[i10 + 1] * fx * (1 - fy) +
      this.stored[i01 + 1] * (1 - fx) * fy +
      this.stored[i11 + 1] * fx * fy;
    return { x: vx, y: vy };
  }

  sampleAmbient(x: number, y: number, time: number, divider: Divider): { x: number; y: number } {
    if (divider.isOval) {
      return this.vortex.sample(x, y, time, divider);
    }
    const divY = divider.yAt(x);
    if (y < divY) {
      return this.upper.sample(x, y, time);
    }
    return this.ocean.sample(x, y, time);
  }

  sample(x: number, y: number, time: number, divider: Divider): { x: number; y: number } {
    const ambient = this.sampleAmbient(x, y, time, divider);
    const stored = this.sampleStored(x, y);
    return { x: ambient.x + stored.x, y: ambient.y + stored.y };
  }

  /** Encode RG as velocity in 0–255 for the distortion shader. */
  writeToImageData(image: ImageData, maxAbs: number): void {
    const data = image.data;
    const scale = maxAbs > 0 ? 127.5 / maxAbs : 0;
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const i = this.cellIndex(col, row);
        const p = (row * this.cols + col) * 4;
        data[p] = clamp(this.stored[i] * scale + 127.5, 0, 255);
        data[p + 1] = clamp(this.stored[i + 1] * scale + 127.5, 0, 255);
        data[p + 2] = 128;
        data[p + 3] = 255;
      }
    }
  }
}

